import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Input, Button, Form, message, Spin } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { SearchOutlined, UserOutlined, LoadingOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import SearchCard from '../components/SearchCard';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';

const Search = () => {
    const { profileId: loggedInProfileId } = useProfile();
    const location = useLocation();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [searchResult, setSearchResult] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (location.pathname !== '/search/by-id') {
            navigate('/search/by-id', { replace: true });
        }
    }, [location.pathname, navigate]);

    const onSearch = async (values) => {
        const { id } = values;
        if (!id) {
            message.error('Please enter a Profile ID to search.');
            return;
        }


        setIsLoading(true);
        setSearchResult(null);

        try {
            const response = await api.post(
                '/profiles/search/by-id',
                { profile_id: loggedInProfileId, search_profile_id: id }
            );

            if (response.data.status === 'success') {
                setSearchResult(response.data.data);
            } else {
                message.error(response.data.message || 'No profile found with that ID.');
            }
        } catch (error) {
            if (error.response?.data?.message) {
                const msg = Object.values(error.response.data.message).flat().join(', ');
                message.error(msg);
            } else {
                message.error('Something went wrong. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{
            fontFamily: 'Calibri, sans-serif',
            height: '100vh',
            overflowY: 'auto',
            background: 'var(--color-bg)',
        }}>
            <Helmet><title>Search Profiles | Oottan</title></Helmet>
            <CustomNavbar />

            <div style={{
                marginTop: '64px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                padding: '60px 16px 40px',
            }}>
                {/* Icon + heading */}
                <div style={{
                    width: '56px', height: '56px', borderRadius: '16px',
                    background: 'var(--color-primary-faint)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    marginBottom: '16px',
                }}>
                    <SearchOutlined style={{ fontSize: '24px', color: 'var(--color-primary)' }} />
                </div>
                <h1 style={{
                    fontSize: '1.5rem', fontWeight: 700,
                    color: 'var(--color-text-primary)',
                    margin: '0 0 6px',
                }}>
                    Search by Profile ID
                </h1>
                <p style={{
                    fontSize: '0.95rem',
                    color: 'var(--color-text-secondary)',
                    margin: '0 0 36px',
                    textAlign: 'center',
                }}>
                    Enter a profile ID to find a specific member
                </p>

                {/* Search form */}
                <div style={{ width: '100%', maxWidth: '480px' }}>
                    <Form form={form} layout="vertical" onFinish={onSearch}>
                        <Form.Item
                            name="id"
                            rules={[{ required: true, message: 'Please enter the Profile ID' }]}
                            style={{ marginBottom: '12px' }}
                        >
                            <Input
                                size="large"
                                placeholder="e.g. HM000000001"
                                prefix={<UserOutlined style={{ color: 'var(--color-text-muted)' }} />}
                                allowClear
                                onChange={(e) => { if (!e.target.value) setSearchResult(null); }}
                                style={{ borderRadius: '10px', fontSize: '1rem' }}
                            />
                        </Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                            block
                            size="large"
                            loading={isLoading}
                            icon={<SearchOutlined />}
                            style={{ borderRadius: '10px', fontWeight: 600, height: '46px', fontSize: '1rem' }}
                        >
                            Search
                        </Button>
                    </Form>

                    {/* Loading */}
                    {isLoading && (
                        <div style={{ textAlign: 'center', padding: '32px 0' }}>
                            <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: 'var(--color-primary)' }} spin />} />
                        </div>
                    )}

                    {/* Result */}
                    {searchResult && !isLoading && (
                        <div style={{ marginTop: '24px' }}>
                            <SearchCard profile={searchResult} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Search;
